import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Shield, Award, Users } from 'lucide-react';
import { useHomepageData } from '../../hooks/useHomepageData';

interface Testimonial {
  name: string;
  role: string;
  content: string;
  rating: number;
  avatar?: string;
}

interface TrustIndicator {
  icon: React.ComponentType<any>;
  label: string;
  description: string;
}

const SocialProofSection: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { data, loading } = useHomepageData();
  const testimonials = data.testimonials;
  const trustIndicators = data.trustIndicators;

  const trustIndicatorIcons = [Shield, Award, Users];

  // 自动轮播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 用户评价区域 */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            用户怎么说
          </h2>
          <p className="text-lg text-gray-600 mb-12">
            来自真实用户的反馈
          </p>

          {/* 评价轮播 */}
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg relative">
              <Quote className="w-8 h-8 text-blue-500 mx-auto mb-6" />
              
              <div className="mb-6">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  "{testimonials[currentTestimonial].content}"
                </p>
                
                <div className="flex justify-center mb-4">
                  {renderStars(testimonials[currentTestimonial].rating)}
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-semibold">
                    {testimonials[currentTestimonial].name.charAt(0)}
                  </span>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">
                    {testimonials[currentTestimonial].name}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {testimonials[currentTestimonial].role}
                  </div>
                </div>
              </div>

              {/* 导航按钮 */}
              <button
                onClick={prevTestimonial}
                className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 touch-target button-press"
                aria-label="上一个评价"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              
              <button
                onClick={nextTestimonial}
                className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 touch-target button-press"
                aria-label="下一个评价"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* 指示器 */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 sm:w-2 sm:h-2 rounded-full transition-colors duration-200 touch-target ${
                    index === currentTestimonial ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                  aria-label={`切换到第${index + 1}个评价`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 信任指标 */}
        <div className="border-t border-gray-200 pt-12">
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-8">
            值得信赖的平台
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustIndicators.map((indicator, index) => {
              const Icon = trustIndicatorIcons[index];
              return (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {indicator.label}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {indicator.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;